import React, { useEffect, useState } from "react";
import Icon from "../Icon";
import Modal from "../Modal";
import { Empty } from "../primitives";
import useImageFallback from "../../lib/useImageFallback";

const SHOWN = 7;

/** Lightbox photo with the same Storage retry as the tiles (a tile that recovered must not open blank). */
function LightboxImage({ url, alt }) {
  const img = useImageFallback(url);
  if (img.failed) return <Icon name="image" size={40} />;
  return <img src={img.src} onError={img.onError} alt={alt} />;
}

function Lightbox({ photos, start, onClose }) {
  const [i, setI] = useState(start);
  const n = photos.length;
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "ArrowLeft") setI((v) => (v - 1 + n) % n);
      else if (e.key === "ArrowRight") setI((v) => (v + 1) % n);
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [n]);
  return (
    <Modal onClose={onClose} size="wide" label={`Photo ${i + 1} of ${n}`} testId="clubDetails.lightbox">
      <div className="lb">
        <LightboxImage key={photos[i]} url={photos[i]} alt={`Club photo ${i + 1}`} />
        <button type="button" className="ibtn" onClick={onClose} aria-label="Close" data-testid="clubDetails.lightbox.close"><Icon name="x" /></button>
        {n > 1 && (
          <>
            <button type="button" className="nav l" onClick={() => setI((v) => (v - 1 + n) % n)} aria-label="Previous photo" data-testid="clubDetails.lightbox.prev"><Icon name="left" /></button>
            <button type="button" className="nav r" onClick={() => setI((v) => (v + 1) % n)} aria-label="Next photo" data-testid="clubDetails.lightbox.next"><Icon name="right" /></button>
            <span className="cnt tnum">{i + 1} / {n}</span>
          </>
        )}
      </div>
    </Modal>
  );
}

/** One mosaic tile; a photo that fails to load (even after the Storage retry) becomes a neutral placeholder. */
function GalleryTile({ url, label, onOpen, testId }) {
  const img = useImageFallback(url);
  return (
    <button type="button" className="ph" aria-label={label} onClick={onOpen} data-testid={testId} style={img.failed ? { background: "var(--wash)" } : { backgroundImage: `url("${img.src}")` }}>
      {img.failed ? <Icon name="image" size={22} /> : <img src={img.src} onError={img.onError} alt="" hidden />}
    </button>
  );
}

/** Gallery tab: mosaic of club.gallery + lightbox (arrow keys, Escape). */
export default function ClubGallery({ gallery }) {
  const photos = [...new Set((Array.isArray(gallery) ? gallery : []).filter((u) => typeof u === "string" && u))];
  const [open, setOpen] = useState(null);
  if (photos.length === 0) return <Empty>No photos yet.</Empty>;
  const extra = photos.length - SHOWN;
  return (
    <>
      <div className="cl-gal">
        {photos.slice(0, SHOWN).map((u, i) => (
          <GalleryTile key={u} url={u} label={`Open photo ${i + 1}`} onOpen={() => setOpen(i)} testId={`clubDetails.photo.${i}`} />
        ))}
        {extra > 0 && (
          <button type="button" className="ph more" onClick={() => setOpen(SHOWN)} data-testid="clubDetails.morePhotos" aria-label={`${extra} more photos`}>+{extra}</button>
        )}
      </div>
      <p className="meta" style={{ marginTop: 8 }}>Select a photo to open it full size.</p>
      {open != null && <Lightbox photos={photos} start={open} onClose={() => setOpen(null)} />}
    </>
  );
}
